'use client';

import { useState, useEffect, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { AlertTriangle, Images } from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { AngleUpload } from '@/features/angle';
import { ConceptAngleType } from '@/shared/constants/concept-angle-type';
import { ReplaceAnglePicker } from './ReplaceAnglePicker';
import { swapAngle } from '../lib/angle-operations';
import type { ConceptAngle } from '@/types/concept';
import type { MediaSourceExtraOption } from '@/shared/ui/MediaSourceSelector';

interface MainAngleReplaceProps {
  projectId: string;
  conceptId: string;
  conceptAngleId: string;
  conceptAngles: ConceptAngle[];
  hasRenders: boolean;
  /**
   * Current count of angles in the project library. Used for pre-flight
   * limit check before upload — replacement creates a new angle record.
   */
  currentProjectAngleLibraryCount?: number;
  onProjectAngleLibraryLimitReached?: () => void;
  onSuccess: () => void;
  onSwapSuccess: () => void;
  onCancel: () => void;
}

export function MainAngleReplace({
  projectId,
  conceptId,
  conceptAngleId,
  conceptAngles,
  hasRenders,
  currentProjectAngleLibraryCount,
  onProjectAngleLibraryLimitReached,
  onSuccess,
  onSwapSuccess,
  onCancel,
}: MainAngleReplaceProps) {
  const t = useTranslations('angle.replace');
  const tCommon = useTranslations('common');
  const [showWarning, setShowWarning] = useState(hasRenders);
  const [showUpload, setShowUpload] = useState(!hasRenders);
  const [showExistingPicker, setShowExistingPicker] = useState(false);

  useEffect(() => {
    setShowWarning(hasRenders);
    setShowUpload(!hasRenders);
    setShowExistingPicker(false);
  }, [hasRenders]);

  const handleConfirmReplace = () => {
    setShowWarning(false);
    setShowUpload(true);
  };

  const handleClose = () => {
    setShowWarning(false);
    setShowUpload(false);
    setShowExistingPicker(false);
    onCancel();
  };

  // "Select from library" option for MediaSourceSelector
  const extraOptions: MediaSourceExtraOption[] = useMemo(
    () => [
      {
        icon: <Images className="w-6 h-6" />,
        title: t('selectFromLibrary'),
        onClick: () => {
          setShowUpload(false);
          setShowExistingPicker(true);
        },
      },
    ],
    [t]
  );

  return (
    <>
      {/* Warning dialog if there are existing renders */}
      <Dialog
        open={showWarning}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{tCommon('replaceMainImage')}</DialogTitle>
          </DialogHeader>
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div className="space-y-3">
              <DialogDescription>{t('warningMessage')}</DialogDescription>
              <DialogDescription>{t('warningConfirm')}</DialogDescription>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              {tCommon('cancel')}
            </Button>
            <Button
              onClick={handleConfirmReplace}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              {t('continueReplacement')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AngleUpload with autoOpen — triggers MediaSourceSelector immediately */}
      {showUpload && (
        <div className="hidden">
          <AngleUpload
            mode="replace"
            projectId={projectId}
            conceptId={conceptId}
            replaceConceptAngleId={conceptAngleId}
            allowMultiple={false}
            angleType={ConceptAngleType.MAIN}
            mediaSourceExtraOptions={extraOptions}
            autoOpen={true}
            currentProjectAngleLibraryCount={currentProjectAngleLibraryCount}
            onProjectAngleLibraryLimitReached={
              onProjectAngleLibraryLimitReached
            }
            onSuccess={() => onSuccess()}
            onCancel={handleClose}
            onError={() => handleClose()}
          />
        </div>
      )}

      {/* Existing angle picker */}
      <ReplaceAnglePicker
        projectId={projectId}
        conceptId={conceptId}
        conceptAngleId={conceptAngleId}
        conceptAngles={conceptAngles}
        open={showExistingPicker}
        onOpenChange={(open) => {
          setShowExistingPicker(open);
          if (!open) onCancel();
        }}
        onSelect={async (selectedAngleId) => {
          await swapAngle(conceptId, conceptAngleId, selectedAngleId);
          onSwapSuccess();
        }}
      />
    </>
  );
}
